import React, { useEffect, useState } from 'react';

import {
  Grid,
  Typography,
  ClickAwayListener,
  IconButton,
  Link,
  Tooltip,
  useMediaQuery,
  CircularProgress,
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import LockSharpIcon from '@mui/icons-material/LockSharp';
import ArrowRightAltOutlinedIcon from '@mui/icons-material/ArrowRightAltOutlined';
import { makeStyles, Theme, useTheme } from '@material-ui/core/styles';
import TooltipCard from '../TooltipCard';
import InfoTooltip from '../../Tooltips/InfoTooltip';
import { infoIconStyle, cardTitle, infoIconButton } from 'src/App/Dashboards/Market/styles/style';
import {
  COLOR_PRIMARY_DARK,
  COLOR_TOOLTIP_BACKGROUND,
  COLOR_BLACK,
  COLOR_PRIMARY_LIGHT,
} from 'src/const';
import {
  DemoFrontViewKeyMetricsCardPropsTypes,
  FrontViewKeyMetricsPropTypes,
} from 'src/interfaces/iproptypes';
import { useHistory } from 'react-router-dom';
import {
  calculateMoMChange,
  calculateRollingYoYChange,
  convertCurrency,
  formatCurrency,
  getSeasonality,
  getSeasonalityBarColor,
  getStartingEndingDate,
  isEmpty,
} from 'src/utils/functions';
import HistoricalModal from 'src/App/Modals/HistoricalModal';
import { useSelector } from 'react-redux';
import { Revenue, TempObject } from 'src/interfaces/idashboard';
import { SelecFilterChip } from 'src/App/Components/Chips';
import {
  COLOR_ERROR_1,
  COLOR_ERROR_BACKGROUNG,
  COLOR_PRIMARY_2,
  COLOR_SUCCESS_1,
  COLOR_SUCCESS_BACKGROUNG,
  COLOR_TEXT_2,
} from 'src/colors';

const useStyles = makeStyles((theme: Theme) => ({
  linearProgress: {
    width: '100%',
    marginBottom: 20,
    color: COLOR_PRIMARY_DARK,
  },
}));

const loadingStyle = makeStyles((theme: Theme) => ({
  colorPrimary: {
    backgroundColor: (props: any) => props.backgroundColor,
  },
  barColorPrimary: {
    backgroundColor: COLOR_TOOLTIP_BACKGROUND,
  },
}));

const DemoFrontViewKeyMetricsCard = ({
  title,
  value,
  tooltip,
  color,
  isDisabled,
  handleOnSampleClick,
  flipCard,
  hasLastMonthData,
  historicalDataLink,
  unit,
  lastMonthData,
  currency,
  multipleMarkersCallLoading,
  renderIn,
  isUserAllowed,
  svgLTMGraph,
  lockedMarketSvgGraphLoadingStatus,
  defaultSvgLtmGraphFallbackImage,
}: DemoFrontViewKeyMetricsCardPropsTypes) => {
  const classes = useStyles();
  const { colorPrimary, barColorPrimary } = loadingStyle({ backgroundColor: color });
  const [isTooltipOpen, setIsTooltipOpen] = useState(false);
  const [metricsKey, setMetricsKey] = useState('');
  const [historicalMonthlyData, setHistoricalMonthlyData] = useState([]);
  const [data, setData] = useState([] as TempObject[]);
  const [metrics, setMetrics] = useState('');
  const [openHistoricalModal, setOpenHistoricalModal] = useState<boolean>(false);
  const [historicalChartTitle, setHistoricalChartTitle] = useState<string>('');
  const [momChange, setMomChange] = useState(0);
  const [yoyChange, setYoyChange] = useState(0);
  const history = useHistory();

  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('xs'));
  const matches = useMediaQuery(theme.breakpoints.down(900));
  const [showGraph, setShowGraph] = useState(false);
  const historicalData = useSelector(state => (state as any).dashboard.historicalData);
  const [seasonalityData, setSeasonalityData] = useState(
    {} as {
      highestRevenue: Revenue;
      lowestRevenue: Revenue;
      seasonality: number;
    }
  );

  useEffect(() => {
    if (title.includes('Revenue')) {
      setMetricsKey('revenue');
      setMetrics('Revenue');
    } else if (title.includes('Occupancy')) {
      setMetricsKey('occupancy_rate');
      setMetrics('Occupancy Rate');
    } else if (title.includes('Nightly')) {
      setMetricsKey('daily_rate');
      setMetrics('Nightly Rate');
    }
  }, []);

  useEffect(() => {
    setHistoricalMonthlyData(historicalData);
  }, [historicalData]);

  useEffect(() => {
    if (historicalMonthlyData) {
      let keys = Object.keys(historicalMonthlyData);
      let tempData: TempObject[] = [];

      for (let i = 0; i < keys.length; i++) {
        let val = historicalMonthlyData[keys[i]]?.[metricsKey + '50'];

        if (val) {
          let tempObj = {
            '0.5': metricsKey != 'occupancy_rate' ? convertCurrency(currency, val) : val,
            '0.9': 0,
            '0.25': 0,
            '0.75': 0,
            [metrics]: metricsKey != 'occupancy_rate' ? convertCurrency(currency, val) : val,
            timePeriod: formatDate(keys[i], true),
            date: keys[i],
          };
          tempData.push(tempObj);
        }
      }
      setData(tempData);
      setShowGraph(true);
    }
  }, [historicalMonthlyData, currency]);

  useEffect(() => {
    if (title.includes('Revenue') && data.length > 0) {
      let seasonalityData = getSeasonality(data.slice(-12));
      setSeasonalityData(seasonalityData);
    }
    if (data.length > 0) {
      const momChange = calculateMoMChange(data, metrics);
      setMomChange(momChange as number);
      const yoyChange = calculateRollingYoYChange(data, metrics);
      setYoyChange(yoyChange as number);
    }
  }, [data]);

  function formatDate(dateString, shortenYear: boolean = false) {
    const months = [
      'Jan',
      'Feb',
      'Mar',
      'Apr',
      'May',
      'Jun',
      'Jul',
      'Aug',
      'Sep',
      'Oct',
      'Nov',
      'Dec',
    ];

    const [year, month] = dateString.split('-');
    const monthName = months[parseInt(month, 10) - 1];

    if (shortenYear) {
      const yearLength = year.length;
      const shortYear = year[yearLength - 2] + year[yearLength - 1];
      return `${monthName} '${shortYear}`;
    }

    return `${monthName} ${year}`;
  }

  // ----------
  const formatValue = (rawKeyMetricsVal: number, updateCurrency = true) => {
    let keyMetricsVal = '';
    if (rawKeyMetricsVal == -1) {
      return title.includes('Occupancy') ? '-' + unit : unit + '-';
    }

    if (isDisabled && lastMonthData && isNaN(parseInt(lastMonthData))) {
      return title.includes('Occupancy') ? '-' + unit : unit + '-';
    }

    if (title.includes('Occupancy') || !updateCurrency) {
      keyMetricsVal = rawKeyMetricsVal.toFixed(0);
    } else {
      keyMetricsVal = convertCurrency(currency, rawKeyMetricsVal).toFixed(0);
    }

    const formattedVal = title.includes('Occupancy')
      ? keyMetricsVal
      : formatCurrency(keyMetricsVal);
    return title.includes('Occupancy') ? formattedVal + unit : unit + formattedVal;
  };

  return (
    <Grid container direction="row" sx={{ height: isSmallScreen ? 280 : 210, background: '#fff' }}>
      {renderIn == 'insights' && !matches && (
        <Grid
          container
          direction="column"
          justifyContent="space-between"
          item
          xs={10}
          sx={{ padding: '16px', minWidth: '100%' }}
        >
          <Grid container direction="row" alignItems="center" justifyContent="space-between">
            <Typography variant="h2" style={cardTitle}>
              {title.includes('Occupancy') ? title.replace('Previous', 'Prev.') : title}
              <ClickAwayListener onClickAway={() => setIsTooltipOpen(false)}>
                <InfoTooltip
                  title={
                    <TooltipCard
                      title={title}
                      content={tooltip.content[0]}
                      contentAdditional={tooltip.content.length > 1 ? tooltip.content[1] : ''}
                      contentAdditional2={tooltip.content.length > 2 ? tooltip.content[2] : ''}
                      link=""
                    />
                  }
                  enterTouchDelay={0}
                  leaveTouchDelay={6000}
                  open={isTooltipOpen}
                  onOpen={() => setIsTooltipOpen(true)}
                  onClose={() => setIsTooltipOpen(false)}
                >
                  <IconButton
                    style={infoIconButton}
                    onClick={() => setIsTooltipOpen(prev => !prev)}
                  >
                    <InfoOutlinedIcon style={infoIconStyle} />
                  </IconButton>
                </InfoTooltip>
              </ClickAwayListener>
            </Typography>

            {showGraph && (
              <div>
                <Typography
                  sx={{
                    fontSize: '12px',
                    paddingRight: '5px',
                    fontWeight: 800,
                    color: COLOR_TEXT_2,
                  }}
                >
                  {data[data.length - 12]?.timePeriod} - {data[data.length - 1]?.timePeriod}
                </Typography>
              </div>
            )}
          </Grid>

          <Grid container sx={{ height: '50%', width: '100%' }}>
            <Grid item xs={5}>
              {isDisabled &&
              !title.includes('Nightly Rate') &&
              !hasLastMonthData &&
              renderIn == 'insights' ? (
                <IconButton onClick={handleOnSampleClick}>
                  <LockSharpIcon style={{ fontSize: 40, color: '#4aa59a' }} />
                </IconButton>
              ) : (
                <>
                  <Typography
                    sx={{ fontWeight: 900, color: color }}
                    variant={isSmallScreen ? 'h6' : 'h4'}
                    component="h3"
                  >
                    {formatValue(value)}
                  </Typography>
                  <div style={{ margin: '10px 0px' }}>
                    <SelecFilterChip
                      height="15px"
                      width="100px"
                      borderRadius="15px"
                      colors={{
                        backgroundColor:
                          momChange > 0 ? COLOR_SUCCESS_BACKGROUNG : COLOR_ERROR_BACKGROUNG,
                        color: momChange > 0 ? COLOR_SUCCESS_1 : COLOR_ERROR_1,
                        borderColor:
                          momChange > 0 ? COLOR_SUCCESS_BACKGROUNG : COLOR_ERROR_BACKGROUNG,
                      }}
                      text={`MoM Change: ${
                        momChange > 0
                          ? `+${momChange}%↑`
                          : `${momChange}%${momChange >= 0 ? '↑' : '↓'}`
                      }`}
                      fontWeight="bold"
                      handleOnClick={() => {}}
                      isSelected={false}
                      tooltip=""
                      fontSize="10px"
                    />
                  </div>
                  {/* <SelecFilterChip
                    height="15px"
                    width="100px"
                    borderRadius="15px"
                    colors={{
                      backgroundColor:
                        yoyChange > 0 ? COLOR_SUCCESS_BACKGROUNG : COLOR_ERROR_BACKGROUNG,
                      color: yoyChange > 0 ? COLOR_SUCCESS_1 : COLOR_ERROR_1,
                      borderColor:
                        yoyChange > 0 ? COLOR_SUCCESS_BACKGROUNG : COLOR_ERROR_BACKGROUNG,
                    }}
                    text={`YoY Change: ${
                      yoyChange > 0
                        ? `+${yoyChange}%↑`
                        : `${yoyChange}%${yoyChange >= 0 ? '↑' : '↓'}`
                    }`}
                    fontWeight="bold"
                    handleOnClick={() => {}}
                    isSelected={false}
                    tooltip=""
                    fontSize="10px"
                  /> */}
                </>
              )}
            </Grid>
            <Grid item xs={1}>
              {title.includes('Revenue') && !isDisabled && !isEmpty(seasonalityData) && (
                <Grid item xs={1} sx={{ height: '100%', width: '100%' }}>
                  <InfoTooltip
                    title={
                      <TooltipCard
                        title={`Seasonality: ${seasonalityData.seasonality}%`}
                        content={`Highest Revenue: ${unit}${formatCurrency(
                          String(
                            convertCurrency(
                              currency,
                              seasonalityData.highestRevenue.revenue as number
                            )
                          )
                        )} (${seasonalityData.highestRevenue.date})`}
                        contentAdditional={`Lowest Revenue: ${unit}${formatCurrency(
                          String(
                            convertCurrency(
                              currency,
                              seasonalityData.lowestRevenue.revenue as number
                            )
                          )
                        )} (${seasonalityData.lowestRevenue.date})`}
                      />
                    }
                  >
                    <div
                      style={{
                        width: 10,
                        borderRadius: 10,
                        height: `${seasonalityData.seasonality}%`,
                        background: getSeasonalityBarColor(seasonalityData.seasonality),
                      }}
                    ></div>
                  </InfoTooltip>
                </Grid>
              )}
            </Grid>
            <Grid item xs={6} alignItems={'center'} sx={{ overflow: 'hidden' }}>
              <div
                style={{
                  position: 'relative',
                  height: '100%',
                  width: '100%',
                  backgroundColor: 'white',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  backgroundImage:
                    lockedMarketSvgGraphLoadingStatus === 'failed'
                      ? `url(${defaultSvgLtmGraphFallbackImage})`
                      : 'none',
                  backgroundSize: '130%',
                  backgroundRepeat: 'no-repeat',
                  backgroundPosition: 'center center',
                  cursor: lockedMarketSvgGraphLoadingStatus === 'failed' ? 'pointer' : 'auto',
                }}
                onClick={() =>
                  lockedMarketSvgGraphLoadingStatus === 'failed' && handleOnSampleClick()
                }
              >
                {lockedMarketSvgGraphLoadingStatus === 'success' ? (
                  <div
                    id={
                      'svg_graph_' +
                      title
                        .toLocaleLowerCase()
                        .split(' ')
                        .join('_')
                    }
                    dangerouslySetInnerHTML={{ __html: svgLTMGraph ? svgLTMGraph : '<svg></svg>' }}
                  />
                ) : lockedMarketSvgGraphLoadingStatus === 'loading' ? (
                  <CircularProgress sx={{ color: color }} />
                ) : (
                  <></>
                )}
              </div>
            </Grid>
          </Grid>

          <Link
            sx={{
              color: COLOR_BLACK,
              textDecoration: 'none',
              ':hover': {
                textDecoration: 'underline',
              },
              marginTop: '20px',
              cursor: 'pointer',
            }}
            onClick={() => {
              if (historicalDataLink) {
                setOpenHistoricalModal(true);
                setHistoricalChartTitle(title);
              } else {
                isDisabled ? handleOnSampleClick() : flipCard('rear');
              }
            }}
          >
            <Tooltip title="See historical data">
              <Typography
                variant={isSmallScreen ? 'caption' : 'body2'}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  flexWrap: 'wrap',
                  justifyContent: 'flex-end',
                  fontSize: '12px',
                  fontWeight: 'bold',
                  color: COLOR_PRIMARY_2,
                  textDecoration: 'underline',
                }}
              >
                View historical trends{' '}
                {isDisabled && (
                  <LockSharpIcon sx={{ color: COLOR_PRIMARY_LIGHT, fontSize: '20px' }} />
                )}{' '}
                <ArrowRightAltOutlinedIcon sx={{ fontSize: '25px' }} />
              </Typography>
            </Tooltip>
          </Link>
        </Grid>
      )}
      {(renderIn == 'estimator' || (renderIn == 'insights' && matches)) && (
        <Grid
          container
          direction="column"
          justifyContent="space-between"
          alignItems="flex-start"
          item
          xs={10}
          sx={{ padding: '16px', paddingRight: 0 }}
        >
          <Grid container direction="row" alignItems="center" justifyContent="space-between">
            <Typography variant="h2" style={cardTitle}>
              {title}
              <ClickAwayListener onClickAway={() => setIsTooltipOpen(false)}>
                <InfoTooltip
                  title={
                    <TooltipCard
                      title={title}
                      content={tooltip.content[0]}
                      contentAdditional={tooltip.content.length > 1 ? tooltip.content[1] : ''}
                      contentAdditional2={tooltip.content.length > 2 ? tooltip.content[2] : ''}
                      link=""
                    />
                  }
                  enterTouchDelay={0}
                  leaveTouchDelay={6000}
                  open={isTooltipOpen}
                  onOpen={() => setIsTooltipOpen(true)}
                  onClose={() => setIsTooltipOpen(false)}
                >
                  <IconButton
                    style={infoIconButton}
                    onClick={() => setIsTooltipOpen(prev => !prev)}
                  >
                    <InfoOutlinedIcon style={infoIconStyle} />
                  </IconButton>
                </InfoTooltip>
              </ClickAwayListener>
            </Typography>
          </Grid>

          {isDisabled &&
          !title.includes('Nightly Rate') &&
          !hasLastMonthData &&
          renderIn == 'insights' ? (
            <IconButton onClick={handleOnSampleClick}>
              <LockSharpIcon style={{ fontSize: 40, color: '#4aa59a' }} />
            </IconButton>
          ) : (
            <Typography
              sx={{ fontWeight: 900, color: color }}
              variant={isSmallScreen ? 'h6' : 'h4'}
              component="h3"
            >
              {formatValue(value)}
            </Typography>
          )}

          <Link
            sx={{
              color: COLOR_BLACK,
              textDecoration: 'none',
              ':hover': {
                textDecoration: 'underline',
              },
              cursor: 'pointer',
            }}
            onClick={() => {
              if (historicalDataLink) {
                setOpenHistoricalModal(true);
                setHistoricalChartTitle(title);
              } else {
                isDisabled ? handleOnSampleClick() : flipCard('rear');
              }
            }}
          >
            <Tooltip title="See historical data">
              <Typography
                variant={isSmallScreen ? 'caption' : 'body2'}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  flexWrap: 'wrap',
                }}
              >
                View historical trends{' '}
                {isDisabled && (
                  <LockSharpIcon sx={{ color: COLOR_PRIMARY_LIGHT, fontSize: '20px' }} />
                )}{' '}
                <ArrowRightAltOutlinedIcon sx={{ fontSize: '25px' }} />
              </Typography>
            </Tooltip>
          </Link>
        </Grid>
      )}
      {openHistoricalModal ? (
        <HistoricalModal
          open={openHistoricalModal}
          historicalChartTitle={historicalChartTitle}
          onClose={() => setOpenHistoricalModal(false)}
          currency={currency}
          handleOnSampleClick={() => {
            setOpenHistoricalModal(false);
            handleOnSampleClick();
          }}
        />
      ) : (
        ''
      )}
    </Grid>
  );
};

export default React.memo(DemoFrontViewKeyMetricsCard);
